import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminPaymentStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatPaise, formatDateTime } from '../../lib/format.js';
import { useAdminPayments } from '../hooks/use-payments.js';
import { PAYMENT_STATUSES, PAYMENT_METHODS, type AdminPaymentListItem, type PaymentListParams } from '../types/payments.js';

const PAGE_SIZE = 20;

export function PaymentsListPage() {
  const [searchInput, setSearchInput] = useState('');
  const customerSearch = useDebouncedValue(searchInput, 300);
  const [orderNumber, setOrderNumber] = useState('');
  const orderNumberDebounced = useDebouncedValue(orderNumber, 300);
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [page, setPage] = useState(0);

  const params: PaymentListParams = useMemo(
    () => ({
      customerSearch: customerSearch || undefined,
      orderNumber: orderNumberDebounced || undefined,
      status: status ? [status as never] : undefined,
      method: method ? [method as never] : undefined,
      sortField: 'createdAt',
      sortDirection: 'desc',
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [customerSearch, orderNumberDebounced, status, method, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminPayments(params);
  const rows = data ?? [];

  const columns: AdminDataTableColumn<AdminPaymentListItem>[] = [
    {
      key: 'orderNumber',
      header: 'Order',
      render: (row) => (
        <Link to={`${ROUTE_NAMES.adminOrders}/${row.orderNumber}`} className="font-medium text-gray-900 hover:underline">
          {row.orderNumber}
        </Link>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.customerName ?? '—'}</p>
          <p className="text-xs text-gray-400">{row.customerEmail}</p>
        </div>
      ),
    },
    { key: 'amountPaise', header: 'Amount', render: (row) => formatPaise(row.amountPaise) },
    { key: 'method', header: 'Method', render: (row) => row.method },
    { key: 'status', header: 'Status', render: (row) => <AdminPaymentStatusBadge status={row.status} /> },
    { key: 'capturedAt', header: 'Captured', render: (row) => (row.capturedAt ? formatDateTime(row.capturedAt) : '—') },
    { key: 'createdAt', header: 'Created', render: (row) => formatDateTime(row.createdAt) },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Payments"
        description="Payment visibility and refund initiation."
        breadcrumbs={[{ label: 'Payments' }]}
        actions={
          <>
            <AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />
            <Link
              to={ROUTE_NAMES.adminRefunds}
              className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              View refunds
            </Link>
          </>
        }
      />

      <AdminSectionCard title="All payments" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={searchInput}
            onChange={(value) => {
              setSearchInput(value);
              setPage(0);
            }}
            placeholder="Search customer name or email…"
            label="Search customer"
          />
          <AdminSearchInput
            value={orderNumber}
            onChange={(value) => {
              setOrderNumber(value);
              setPage(0);
            }}
            placeholder="Order number…"
            label="Search order number"
          />
          <AdminSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(0);
            }}
            allLabel="All statuses"
            options={PAYMENT_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
          />
          <AdminSelect
            label="Method"
            value={method}
            onChange={(value) => {
              setMethod(value);
              setPage(0);
            }}
            allLabel="All methods"
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
          />
        </AdminFilterBar>

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No payments match these filters"
          rowActions={(row) => (
            <Link
              to={`${ROUTE_NAMES.adminPayments}/${row.id}`}
              className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
            >
              View
            </Link>
          )}
        />

        <nav aria-label="Pagination" className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </AdminSectionCard>
    </AdminContent>
  );
}
